// /frontend/src/pages/cart/CartPage.jsx - Modified for RFQ Mode
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Layout from '../../components/layout/Layout';
import { FaTrash, FaArrowLeft } from 'react-icons/fa';
import { cartService } from '../../services/cartService';
import { isRFQModeEnabled, shouldHidePrices, getRFQTexts, getNotificationConfig } from '../../config/rfqConfig';

const CartPage = () => {
  const [cart, setCart] = useState({ items: [], totalItems: 0, totalPrice: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // RFQ Configuration
  const isRFQMode = isRFQModeEnabled();
  const hidePrices = shouldHidePrices();
  const rfqTexts = getRFQTexts();
  const notificationConfig = getNotificationConfig();
  
  // Add minimum order constant - only used if not in RFQ mode
  const MINIMUM_ORDER_AMOUNT = 500;
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // Fetch cart data
  useEffect(() => {
    const fetchCart = async () => {
      try {
        if (!isAuthenticated) {
          setCart({ items: [], totalItems: 0, totalPrice: 0 });
          navigate('/login?redirect=/cart');
          return;
        }
        
        setLoading(true);
        const cartData = await cartService.getCart();
        console.log('Cart data received:', cartData);
        
        setCart({
          ...cartData,
          items: cartData?.items || []
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching cart:', err);
        setError(err.message || 'Failed to load cart');
        setLoading(false);
        setCart({ items: [], totalItems: 0, totalPrice: 0 });
      }
    };
    
    fetchCart();
  }, [isAuthenticated, navigate]);
  
  // Debug cart content when it changes
  useEffect(() => {
    console.log('Current cart state:', cart);
    console.log('RFQ Mode enabled:', isRFQMode);
    if (cart?.items?.length > 0) {
      const firstProduct = cart.items[0].product;
      console.log('First product in cart:', firstProduct);
      if (firstProduct) {
        console.log('Product images:', firstProduct.images);
      }
    }
  }, [cart]);
  
  // Listen for cart reset events
  useEffect(() => {
    const handleCartReset = () => {
      console.log('Cart reset event received - clearing cart state');
      setCart({ items: [], totalItems: 0, totalPrice: 0 });
    };
    
    window.addEventListener('cart-reset', handleCartReset);
    
    return () => {
      window.removeEventListener('cart-reset', handleCartReset);
    };
  }, []);
  
  // Update item quantity
  const updateItemQuantity = async (itemId, newQuantity) => {
    try {
      setUpdating(true);
      await cartService.updateCartItem(itemId, newQuantity);
      
      setCart(prevCart => {
        if (!prevCart?.items) return { items: [], totalItems: 0, totalPrice: 0 };
        
        const targetItem = prevCart.items.find(item => item._id === itemId);
        if (!targetItem) return prevCart;
        
        const quantityChange = newQuantity - targetItem.quantity;
        
        // For RFQ mode, we still calculate prices internally but don't show them
        const itemPrice = targetItem.product.isOnSale 
          ? targetItem.product.salePrice 
          : targetItem.product.price;
        
        return {
          ...prevCart,
          items: prevCart.items.map(item => 
            item._id === itemId ? { ...item, quantity: newQuantity } : item
          ),
          totalItems: (prevCart.totalItems || 0) + quantityChange,
          totalPrice: prevCart.items.reduce((total, item) => {
            const price = item.product.isOnSale 
              ? item.product.salePrice 
              : item.product.price;
            
            return total + (item._id === itemId 
              ? (price * newQuantity) 
              : (price * item.quantity)
            );
          }, 0)
        };
      });
      
      setUpdating(false);
    } catch (err) {
      console.error('Error updating item:', err);
      setError(err.message || 'Failed to update item');
      setUpdating(false);
    }
  };
  
  // Remove item from cart
  const removeItem = async (itemId) => {
    try {
      setUpdating(true);
      await cartService.removeFromCart(itemId);
      
      setCart(prevCart => {
        if (!prevCart?.items) return { items: [], totalItems: 0, totalPrice: 0 };
        
        const removedItem = prevCart.items.find(item => item._id === itemId);
        if (!removedItem) return prevCart;
        
        const itemPrice = removedItem.product.isOnSale 
          ? removedItem.product.salePrice 
          : removedItem.product.price;
        
        return {
          ...prevCart,
          items: prevCart.items.filter(item => item._id !== itemId),
          totalItems: (prevCart.totalItems || 0) - removedItem.quantity,
          totalPrice: (prevCart.totalPrice || 0) - (itemPrice * removedItem.quantity)
        };
      });
      
      setUpdating(false);
    } catch (err) {
      console.error('Error removing item:', err);
      setError(err.message || 'Failed to remove item');
      setUpdating(false);
    }
  };
  
  // Calculate total price (for internal use)
  const calculateTotal = () => {
    if (!cart?.items?.length) return 0;
    
    return cart.items.reduce((total, item) => {
      const itemPrice = item.product.isOnSale 
        ? item.product.salePrice 
        : item.product.price;
      
      return total + (itemPrice * item.quantity);
    }, 0);
  };
  
  // Check if order meets minimum requirement (only for non-RFQ mode)
  const meetsMinimumOrder = () => {
    if (isRFQMode) return true; // No minimum order for RFQ
    const total = calculateTotal();
    return total >= MINIMUM_ORDER_AMOUNT;
  };
  
  // Handle checkout
  const handleCheckout = () => {
    if (!isRFQMode) {
      // Original logic for non-RFQ mode
      if (!meetsMinimumOrder()) {
        setError(`Minimum order amount is $${MINIMUM_ORDER_AMOUNT.toFixed(2)}`);
        return;
      }
    }
    
    setError(null);
    navigate('/shipping');
  };
  
  // If not authenticated, show login message
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Please Login to View Your Cart</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view your cart and checkout.</p>
          <Link 
            to="/login?redirect=/cart" 
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Login Now
          </Link>
        </div>
      </Layout>
    );
  }
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-48 bg-gray-200 rounded mb-6"></div>
            <div className="h-32 bg-gray-200 rounded w-1/3 ml-auto"></div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Cart</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }
  
  // Calculate the current total
  const currentTotal = calculateTotal();
  const orderMeetsMinimum = meetsMinimumOrder();
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isRFQMode ? 'Your Quote Request' : 'Your Cart'}
          </h1>
          <Link 
            to="/browse" 
            className="inline-flex items-center text-teal-800 hover:text-teal-900 font-medium"
          >
            <FaArrowLeft className="mr-2" /> Continue Shopping
          </Link>
        </div>
        
        {/* RFQ Mode Banner */}
        {isRFQMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <h3 className="font-medium text-blue-800">Request for Quotation Mode</h3>
                <p className="text-blue-700 text-sm">Items in your cart will be used for a quote request. Custom pricing will be provided in your quotation.</p>
              </div>
            </div>
          </div>
        )}
        
        {!cart?.items?.length ? (
          <div className="text-center py-16 border border-gray-200 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isRFQMode ? 'No items for quote request' : 'Your cart is empty'}
            </h2>
            <p className="text-gray-600 mb-6">
              {isRFQMode 
                ? 'Add items to create a quote request.' 
                : 'Looks like you haven\'t added any items to your cart yet.'
              }
            </p>
            <Link 
              to="/browse" 
              className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      {!hidePrices && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      {!hidePrices && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      )}
                      {isRFQMode && (
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quote Status
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cart.items.map((item) => {
                      if (!item.product) return null;
                      
                      const price = item.product.isOnSale 
                        ? item.product.salePrice 
                        : item.product.price;
                      
                      return (
                        <tr key={item._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-16 w-16 bg-gray-100 rounded overflow-hidden mr-4">
                                {item.product.images && item.product.images.length > 0 ? (
                                  <img 
                                    src={item.product.images[0]} 
                                    alt={item.product.name} 
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '/placeholder-image.jpg';
                                    }}
                                  />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center text-gray-400">
                                    No Image
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 mb-1">{item.product.name}</div>
                                {item.variants && Object.keys(item.variants).length > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {Object.entries(item.variants).map(([key, value]) => (
                                      <div key={key}>{key}: {value}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {!hidePrices && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              ${price.toFixed(2)}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex border border-gray-300 rounded overflow-hidden w-24">
                              <button 
                                onClick={() => updateItemQuantity(item._id, Math.max(1, item.quantity - 1))}
                                disabled={updating}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItemQuantity(item._id, Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-full text-center border-0 focus:ring-0 text-sm"
                                min="1"
                                disabled={updating}
                              />
                              <button 
                                onClick={() => updateItemQuantity(item._id, item.quantity + 1)}
                                disabled={updating}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          {!hidePrices && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              ${(price * item.quantity).toFixed(2)}
                            </td>
                          )}
                          {isRFQMode && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Pending Quote
                              </span>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                              onClick={() => removeItem(item._id)}
                              disabled={updating}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Cart Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                {!isRFQMode ? (
                  // Normal cart summary
                  <>
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h2>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">${calculateTotal().toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping</span>
                        <span className="font-medium">Calculated at checkout</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax</span>
                        <span className="font-medium">Calculated at checkout</span>
                      </div>
                      
                      <div className="border-t border-gray-200 pt-3 flex justify-between">
                        <span className="text-gray-900 font-medium">Estimated Total</span>
                        <span className="text-gray-900 font-bold">${calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Minimum order warning */}
                    {!orderMeetsMinimum && cart?.items?.length > 0 && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm font-medium">
                          Minimum order amount is ${MINIMUM_ORDER_AMOUNT.toFixed(2)}. 
                          You need to add ${(MINIMUM_ORDER_AMOUNT - currentTotal).toFixed(2)} more 
                          to proceed to checkout.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  // RFQ cart summary
                  <>
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Quote Request Summary</h2>
                    
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <div className="flex items-center mb-2">
                        <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <h4 className="font-medium text-blue-800">Custom Quote Request</h4>
                      </div>
                      <p className="text-blue-700 text-sm">
                        Proceed to submit your quote request. Our team will provide detailed pricing within {notificationConfig.estimatedResponseTime}.
                      </p>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Items:</span>
                        <span className="font-medium">{cart.items.length} item(s)</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Quantity:</span>
                        <span className="font-medium">{cart.items.reduce((total, item) => total + item.quantity, 0)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Quote Status:</span>
                        <span className="font-medium text-orange-600">Ready to Submit</span>
                      </div>
                      
                      <div className="border-t border-gray-200 pt-3 flex justify-between">
                        <span className="text-gray-900 font-medium">Price:</span>
                        <span className="text-blue-600 font-bold">Quote Required</span>
                      </div>
                    </div>
                  </>
                )}
                
                <button 
                  onClick={handleCheckout}
                  disabled={updating || !cart?.items?.length || (!isRFQMode && !orderMeetsMinimum)}
                  className={`w-full py-3 px-4 rounded-md font-medium text-white ${
                    updating || !cart?.items?.length || (!isRFQMode && !orderMeetsMinimum)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : isRFQMode
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-teal-800 hover:bg-teal-900'
                  } transition-colors`}
                >
                  {updating 
                    ? 'Updating...' 
                    : cart?.items?.length === 0 
                      ? 'Cart is Empty' 
                      : isRFQMode 
                        ? 'Request Quote'
                        : 'Proceed to Checkout'
                  }
                </button>
                
                <div className="mt-4 text-xs text-gray-500 text-center">
                  {isRFQMode ? (
                    'No payment required at this time. Quote will be provided via email.'
                  ) : (
                    'Taxes and shipping calculated at checkout'
                  )}
                </div>
                
                {/* Contact information for RFQ */}
                {isRFQMode && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Questions? Contact us at <strong>{notificationConfig.contactEmail}</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CartPage;